import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeftIcon, BuildingOffice2Icon, TableCellsIcon, DocumentTextIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { fairLendingApi, firmsApi } from '../lib/api';
import RiskBadge from '../components/fair-lending/RiskBadge';
import FldMatrix from '../components/fld/FldMatrix';
import FldSummaryTab from '../components/fld/FldSummaryTab';
import FldOverviewTab, { type FldOverview } from '../components/fld/FldOverviewTab';
import { M6Matrix } from '../components/fld/M6Matrix';
import { R4Matrix } from '../components/fld/R4Matrix';
import { R9Matrix } from '../components/fld/R9Matrix';
import { MmctBlocksTable } from '../components/fld/MmctBlocksTable';
import { R12Matrix } from '../components/fld/R12Matrix';
import FldDrillDownModal from '../components/fld/FldDrillDownModal';
import BranchImportModal from '../components/fld/BranchImportModal';
import PartnerReportModal from '../components/fld/PartnerReportModal';
import R1Distribution from '../components/fld/R1Distribution';
import { useTeamId } from '../hooks/useTeamId';

// ─── Tab Configuration ──────────────────────────────────────────────────────

type AspectTab = 'summary' | 'overview' | 'underwriting' | 'pricing' | 'steering' | 'redlining' | 'marketing';

// Placement is Michael Wallace's, 2026-09-13: "to the right of 'Summary of Risks' and to the left
// of 'Underwriting.'" It reads in the order the work is done — what the file is made of, then the
// disparities drawn from it.
const ASPECT_TABS: { key: AspectTab; label: string }[] = [
  { key: 'summary', label: 'Summary of Risks' },
  { key: 'overview', label: 'Overview – All Apps' },
  { key: 'underwriting', label: 'Underwriting' },
  { key: 'pricing', label: 'Pricing' },
  { key: 'steering', label: 'Steering' },
  { key: 'redlining', label: 'Redlining' },
  { key: 'marketing', label: 'Marketing' },
];

const RISK_FACTOR_TABS: Record<string, { key: string; label: string; isMean?: boolean; meanDecimals?: number; meanUnit?: string; meanCurrency?: boolean }[]> = {
  underwriting: [
    { key: 'U1_DENIAL', label: 'U1: Denials' },
    { key: 'U2_PROCESSING_TIME', label: 'U2: Processing Time', isMean: true, meanDecimals: 0, meanUnit: 'avg. days' },
    { key: 'U3_ANA', label: 'U3: ANA' },
    { key: 'U3_INCOMPLETE', label: 'U3: Incomplete' },
    { key: 'U3_WITHDRAWN', label: 'U3: Withdrawn' },
    { key: 'U3_PREAPPROVAL_DENIED', label: 'U3: Preapproval Denied' },
    { key: 'U3_PREAPPROVAL_ANA', label: 'U3: Preapproval ANA' },
  ],
  pricing: [
    { key: 'P4_INTEREST', label: 'P4: Interest Rate', isMean: true },
    { key: 'P4_OC', label: 'P4: Orig. Charges', isMean: true, meanCurrency: true },
    { key: 'P4_TLC', label: 'P4: Total Loan Costs', isMean: true, meanCurrency: true },
    { key: 'P6_NEG_INCIDENCE', label: 'P6: Neg. RS - Inc.' },
    { key: 'P6_NEG_AVG_SIZE', label: 'P6: Neg. RS - Avg. Size', isMean: true },
    { key: 'P6_POS_INCIDENCE', label: 'P6: Pos. RS - Inc.' },
    { key: 'P6_POS_AVG_SIZE', label: 'P6: Pos. RS - Avg. Size', isMean: true },
  ],
  steering: [
    { key: 'S4_LOAN_TYPE', label: 'S4: FHA Loan Type' },
    { key: 'S4_INTEREST_ONLY', label: 'S4: Interest-Only' },
    { key: 'S4_NEG_AM', label: 'S4: Neg. Amortization' },
    { key: 'S4_REVERSE_MORTGAGE', label: 'S4: Reverse Mortgage' },
    { key: 'S4_OTHER_NON_AM', label: 'S4: Other Non-am.' },
    { key: 'S4_BALLOON', label: 'S4: Balloon' },
    { key: 'S4_HOEPA', label: 'S4: HOEPA' },
    { key: 'S4_PREPAYMENT_PENALTY', label: 'S4: Prepayment Penalty' },
    { key: 'S4_INTRO_RATE_PERIOD', label: 'S4: Intro Rate Period' },
    { key: 'S5_AFFILIATES', label: 'S5: Affiliates' },
    { key: 'S6_PORTFOLIO_CHANNEL', label: 'S6: Portfolio Channel' },
    { key: 'S6_PREAPPROVAL', label: 'S6: Preapproval' },
  ],
  redlining: [
    { key: 'R1_MMCT', label: 'R1: MMCT Distribution' },
    { key: 'R2_MMCT_DENIAL', label: 'R2: MMCT Denials' },
    { key: 'R3_COLLATERAL', label: 'R3: Collateral' },
    { key: 'R4_REVERSE', label: 'R4: Neg. Features' },
    { key: 'R9_AA_REMA', label: 'R9: AA / REMA' },
    { key: 'R12_BRANCH', label: 'R12: Branch Dist.' },
  ],
  marketing: [
    { key: 'M6_MARKETING', label: 'M6: Demographics' },
  ],
};

// Open-End Line of Credit split (Michael Wallace, 2026-10-23): NOELC (code 2) before OELC (code 1).
type OpenEndVariant = 'NOT_OPEN_END' | 'OPEN_END';
const OPEN_END_ORDER: OpenEndVariant[] = ['NOT_OPEN_END', 'OPEN_END'];
const OPEN_END_LABEL: Record<OpenEndVariant, string> = { NOT_OPEN_END: 'Not Open-End (NOELC)', OPEN_END: 'Open-End (OELC)' };
// Lending Channel of the Pricing tables, from Type of Purchaser (Michael Wallace, 2026-11-01).
type ChannelView = 'ALL' | 'PORTFOLIO' | 'SECONDARY_MARKET';
const CHANNEL_ORDER: ChannelView[] = ['ALL', 'PORTFOLIO', 'SECONDARY_MARKET'];
const CHANNEL_LABEL: Record<ChannelView, string> = { ALL: 'All', PORTFOLIO: 'Portfolio', SECONDARY_MARKET: 'Secondary Market' };

// ─── Page Component ─────────────────────────────────────────────────────────

export default function FldDashboardPage() {
  const { id } = useParams<{ id: string }>();
  const { teamId } = useTeamId();
  const [aspectTab, setAspectTab] = useState<AspectTab>('summary');
  const [riskFactorTab, setRiskFactorTab] = useState<string>('');
  const [drillDown, setDrillDown] = useState<{ riskFactor: string; cell: any; lienVariant?: 'SUBORDINATE'; openEndVariant?: OpenEndVariant; channelVariant?: 'PORTFOLIO' | 'SECONDARY_MARKET' } | null>(null);
  // All / Portfolio / Secondary Market for the Pricing tables, when the run computed channel tables.
  const [channelView, setChannelView] = useState<ChannelView>('ALL');
  // First Lien / Subordinate Lien view for the Underwriting and Pricing tables (2026-10-19).
  const [lienView, setLienView] = useState<'FIRST' | 'SUBORDINATE'>('FIRST');
  // Not Open-End / Open-End view when the run splits U and P by open-end (Michael Wallace, 2026-10-23).
  // Not Open-End first: "It's typical to have more volume in NOE."
  const [openEndView, setOpenEndView] = useState<OpenEndVariant>('NOT_OPEN_END');
  const [branchModalOpen, setBranchModalOpen] = useState(false);
  const [exporting, setExporting] = useState<null | 'xlsx' | 'docx' | 'flra'>(null);
  const [clientReportOpen, setClientReportOpen] = useState(false);

  // Consulting firms the user is an explicit member of (CLARID-SPEC-PBR-001). Most users have none,
  // and only firm members see the Client report button.
  const { data: firms = [] } = useQuery({
    queryKey: ['firms', 'mine'],
    queryFn: async () => (await firmsApi.mine()).data.firms,
    staleTime: 5 * 60 * 1000,
  });

  const handleExportFlra = async () => {
    const validationId = data?.validationId as string | undefined;
    if (exporting || !teamId || !validationId) return;
    setExporting('flra');
    try {
      const res = await fairLendingApi.exportFlraHmda(teamId, validationId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const cd = (res.headers as any)['content-disposition'] as string | undefined;
      const match = cd?.match(/filename="?([^"]+)"?/);
      a.download = match?.[1] || 'FLRA_HMDA_Tables.docx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('FLRA HMDA tables exported');
    } catch {
      toast.error('FLRA export failed');
    } finally {
      setExporting(null);
    }
  };

  const handleExport = async (format: 'xlsx' | 'docx') => {
    if (exporting || !teamId || !id) return;
    setExporting(format);
    try {
      const res = await fairLendingApi.exportFld(teamId, id, format);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const cd = (res.headers as any)['content-disposition'] as string | undefined;
      const match = cd?.match(/filename="?([^"]+)"?/);
      a.download = match?.[1] || `fair-lending-fld.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`FLD ${format === 'xlsx' ? 'Excel' : 'Word'} exported`);
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(null);
    }
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['fld-analysis', teamId, id],
    queryFn: async () => {
      const res = await fairLendingApi.getFldAnalysis(teamId!, id!);
      return res.data.analysis;
    },
    enabled: !!id && !!teamId,
    // The analysis now runs on a background worker (P1.9). Poll while it's queued/running, stop once
    // it settles.
    refetchInterval: (query) => {
      const status = (query.state.data as any)?.status;
      return status === 'PENDING' || status === 'RUNNING' ? 5000 : false;
    },
  });

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-64 bg-panel-2 rounded" />
        <div className="h-96 bg-panel-2 rounded-lg" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Failed to load FLD analysis.</p>
        <Link to="/fair-lending" className="mt-4 text-sm text-teal-600 hover:underline">
          Back to Dashboard
        </Link>
      </div>
    );
  }

  // The analysis is queued or running on the worker — poll (above) and show progress until it lands.
  if (data.status === 'PENDING' || data.status === 'RUNNING') {
    return (
      <div className="text-center py-16">
        <div className="mx-auto h-10 w-10 rounded-full border-4 border-teal-200 border-t-teal-600 animate-spin" />
        <h2 className="mt-4 text-lg font-semibold text-ink">Running Fair Lending analysis…</h2>
        <p className="mt-2 text-sm text-ink-4 max-w-md mx-auto">
          Enriching the LAR and running all 17 risk factors. This can take a minute — the results
          appear here automatically when it finishes.
        </p>
        <Link to="/fair-lending" className="mt-4 inline-block text-sm text-teal-600 hover:underline">
          Back to Dashboard
        </Link>
      </div>
    );
  }

  if (data.status === 'FAILED') {
    return (
      <div className="text-center py-16">
        <p className="text-red-600 font-medium">Fair Lending analysis failed.</p>
        {data.errorMessage && <p className="mt-2 text-sm text-ink-4">{data.errorMessage}</p>}
        <Link to="/fair-lending" className="mt-4 inline-block text-sm text-teal-600 hover:underline">
          Back to Dashboard
        </Link>
      </div>
    );
  }

  const result = data.resultJson as any;
  const tabs = result?.tabs ?? [];
  const summaryOfRisks = result?.summaryOfRisks ?? [];
  // Absent on results stored before the Overview shipped — FldOverviewTab renders a re-run prompt.
  const overview = result?.overview as FldOverview | undefined;
  const appliedFilters: string[] = result?.appliedFilters ?? [];
  const enrichmentStats = result?.enrichmentStats ?? {};

  // Get the active tab's risk factor options
  const activeFactorTabs = RISK_FACTOR_TABS[aspectTab] ?? [];
  const activeFactorKey = riskFactorTab || activeFactorTabs[0]?.key || '';
  // A run with subordinate lien tables carries two tabs per Underwriting / Pricing factor; the switch
  // picks one. Factors without a subordinate twin always show their only tab.
  const hasSubordinateTab = tabs.some((t: any) => t.riskFactor === activeFactorKey && t.lienVariant === 'SUBORDINATE');
  const showSubordinate = hasSubordinateTab && lienView === 'SUBORDINATE';
  // A split run carries up to four tabs per factor (CFL/NOELC, CFL/OELC, CSL/NOELC, CSL/OELC). An empty
  // population's tab is left out by the engine, so the open-end choice falls back to what this lien has.
  const lienTabs = tabs.filter((t: any) =>
    t.riskFactor === activeFactorKey && (showSubordinate ? t.lienVariant === 'SUBORDINATE' : !t.lienVariant));
  const openEndOptions = OPEN_END_ORDER.filter(v => lienTabs.some((t: any) => t.openEndVariant === v));
  const effectiveOpenEnd = openEndOptions.includes(openEndView) ? openEndView : openEndOptions[0];
  const variantTab = openEndOptions.length
    ? lienTabs.find((t: any) => t.openEndVariant === effectiveOpenEnd)
    : lienTabs[0];
  // The same Pricing table on one Lending Channel: same factor, lien and open-end side, from channelTabs.
  const channelTabs: any[] = (result?.channelTabs ?? []).filter((t: any) =>
    variantTab && t.riskFactor === variantTab.riskFactor &&
    (t.lienVariant ?? null) === (variantTab.lienVariant ?? null) && (t.openEndVariant ?? null) === (variantTab.openEndVariant ?? null));
  const channelOptions = channelTabs.length ? CHANNEL_ORDER.filter((v) => v === 'ALL' || channelTabs.some((t) => t.channelVariant === v)) : [];
  const effectiveChannel = channelOptions.includes(channelView) ? channelView : 'ALL';
  const activeTab = effectiveChannel === 'ALL' ? variantTab : channelTabs.find((t) => t.channelVariant === effectiveChannel);
  const activeFactorConfig = activeFactorTabs.find(f => f.key === activeFactorKey);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/fair-lending" className="text-ink-4 hover:text-ink-3">
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-ink">Fair Lending Dashboard (FLD)</h1>
            <p className="text-sm text-ink-3">
              IFLEP Analysis — {new Set(tabs.map((t: any) => t.riskFactor)).size} risk factors analyzed{result?.subordinateLienIncluded ? ' · first and subordinate lien' : ''}{result?.openEndSplit ? ' · split by open-end' : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => handleExport('xlsx')}
            disabled={!!exporting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border border-rule text-ink-2 hover:bg-panel-2 disabled:opacity-50"
          >
            <TableCellsIcon className="h-4 w-4" />
            {exporting === 'xlsx' ? 'Exporting…' : 'Excel'}
          </button>
          <button
            onClick={() => handleExport('docx')}
            disabled={!!exporting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border border-rule text-ink-2 hover:bg-panel-2 disabled:opacity-50"
          >
            <DocumentTextIcon className="h-4 w-4" />
            {exporting === 'docx' ? 'Exporting…' : 'Word'}
          </button>
          <button
            onClick={handleExportFlra}
            disabled={!!exporting}
            title="Generate the FLRA HMDA disparity-table set (Tables 11–35) in Wallace's layout"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border border-rule text-ink-2 hover:bg-panel-2 disabled:opacity-50"
          >
            <DocumentTextIcon className="h-4 w-4" />
            {exporting === 'flra' ? 'Generating…' : 'FLRA Tables'}
          </button>
          {firms.length > 0 && (
            <button
              onClick={() => setClientReportOpen(true)}
              disabled={!!exporting}
              title="Generate your firm's branded Fair Lending Data Analyses report for this bank (Word)"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border border-rule text-ink-2 hover:bg-panel-2 disabled:opacity-50"
            >
              <DocumentArrowDownIcon className="h-4 w-4" />
              Client report
            </button>
          )}
          <button
            onClick={() => setBranchModalOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border border-rule text-ink-2 hover:bg-panel-2"
          >
            <BuildingOffice2Icon className="h-4 w-4" />
            Branches
          </button>
          <RiskBadge level={result?.overallRiskLevel ?? 'LOW'} />
        </div>
      </div>

      {/* Geography coverage — loud banner (P0.10): unresolved tracts read as Non-MMCT/outside-AA,
          so low coverage silently understates MMCT/redlining/inside-AA. Warn prominently, don't block. */}
      {result?.geographyCoverage?.belowThreshold && (
        <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <span className="text-red-600 text-xl leading-none">⚠</span>
            <div>
              <h3 className="text-sm font-semibold text-red-800">
                Geography coverage {Math.round(result.geographyCoverage.coveragePct * 100)}% — results understated
              </h3>
              <p className="text-sm text-red-700 mt-0.5">
                {result.geographyCoverage.unresolved} of {result.geographyCoverage.total} records have no resolvable
                census tract, so MMCT, redlining, and inside-AA figures are <strong>understated</strong> — unresolved
                tracts count as Non-MMCT / outside-AA. Load the correct FFIEC census vintage for this LAR year before
                relying on or exporting these results.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <StatCard label="Total Records" value={enrichmentStats.totalRecords ?? 0} />
        {!!enrichmentStats.purchasedLoans && (
          <StatCard label="Purchased (excluded)" value={enrichmentStats.purchasedLoans} />
        )}
        <StatCard label="CFLPRSB" value={enrichmentStats.cflprsbRecords ?? 0} />
        <StatCard label="Originations" value={enrichmentStats.originationsOnly ?? 0} />
        <StatCard label="MMCT Records" value={enrichmentStats.mmctRecords ?? 0} />
        <StatCard label="Elderly" value={enrichmentStats.elderlyRecords ?? 0} />
        <StatCard
          label="Findings"
          value={summaryOfRisks.length}
          highlight={summaryOfRisks.length > 0}
        />
      </div>

      {/* Limitations */}
      {result?.limitations?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-amber-800 mb-1">Data Limitations</h3>
          <ul className="text-sm text-amber-700 space-y-0.5">
            {result.limitations.map((l: string, i: number) => (
              <li key={i}>• {l}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Aspect of Credit Tabs */}
      <div className="border-b border-rule">
        <nav className="flex -mb-px space-x-6">
          {ASPECT_TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => {
                setAspectTab(tab.key);
                setRiskFactorTab('');
              }}
              className={clsx(
                'py-3 px-1 text-sm font-medium border-b-2 transition-colors',
                aspectTab === tab.key
                  ? 'border-teal-600 text-teal-600'
                  : 'border-transparent text-ink-3 hover:text-ink-2 hover:border-rule',
              )}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content Area */}
      {aspectTab === 'summary' ? (
        <div className="bg-panel rounded-lg border p-6">
          <h2 className="text-lg font-semibold text-ink mb-4">Summary of Risks</h2>
          <FldSummaryTab risks={summaryOfRisks} />
        </div>
      ) : aspectTab === 'overview' ? (
        <div className="bg-panel rounded-lg border p-6">
          <FldOverviewTab overview={overview} appliedFilters={appliedFilters} />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Risk Factor Sub-tabs */}
          {activeFactorTabs.length > 1 && (
            <div className="flex gap-2 flex-wrap">
              {activeFactorTabs.map(ft => (
                <button
                  key={ft.key}
                  onClick={() => setRiskFactorTab(ft.key)}
                  className={clsx(
                    'px-3 py-1.5 text-sm rounded-md border transition-colors',
                    (activeFactorKey === ft.key)
                      ? 'bg-teal-600 text-white border-teal-600'
                      : 'bg-panel text-ink-2 border-rule hover:bg-panel-2',
                  )}
                >
                  {ft.label}
                </button>
              ))}
            </div>
          )}

          {hasSubordinateTab && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-ink-3">Lien:</span>
              {(['FIRST', 'SUBORDINATE'] as const).map(v => (
                <button
                  key={v}
                  onClick={() => setLienView(v)}
                  className={clsx(
                    'px-3 py-1 rounded-md border transition-colors',
                    lienView === v ? 'bg-ink text-white border-ink' : 'bg-panel text-ink-2 border-rule hover:bg-panel-2',
                  )}
                >
                  {v === 'FIRST' ? 'First Lien' : 'Subordinate Lien'}
                </button>
              ))}
            </div>
          )}

          {openEndOptions.length > 0 && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-ink-3">Open-End:</span>
              {openEndOptions.map(v => (
                <button
                  key={v}
                  onClick={() => setOpenEndView(v)}
                  className={clsx(
                    'px-3 py-1 rounded-md border transition-colors',
                    effectiveOpenEnd === v ? 'bg-ink text-white border-ink' : 'bg-panel text-ink-2 border-rule hover:bg-panel-2',
                  )}
                >
                  {OPEN_END_LABEL[v]}
                </button>
              ))}
            </div>
          )}

          {channelOptions.length > 0 && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-ink-3">Lending Channel:</span>
              {channelOptions.map(v => (
                <button
                  key={v}
                  onClick={() => setChannelView(v)}
                  className={clsx(
                    'px-3 py-1 rounded-md border transition-colors',
                    effectiveChannel === v ? 'bg-ink text-white border-ink' : 'bg-panel text-ink-2 border-rule hover:bg-panel-2',
                  )}
                >
                  {CHANNEL_LABEL[v]}
                </button>
              ))}
              {result?.lendingChannelCounts && (
                <span className="text-xs text-ink-4">
                  Originations: {result.lendingChannelCounts.portfolio.toLocaleString()} portfolio, {result.lendingChannelCounts.secondaryMarket.toLocaleString()} secondary market
                </span>
              )}
            </div>
          )}

          {/* Matrix */}
          {activeTab ? (
            <div className="bg-panel rounded-lg border p-6">
              {/*
                R1's stored tab is the older per-loan-purpose analyzer Michael crossed out; its label,
                risk badge and notes could contradict the R1 screen below, which carries its own heading.
              */}
              {activeFactorKey !== 'R1_MMCT' && (
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-ink">{activeTab.label}</h2>
                  <p className="text-sm text-ink-3">{activeTab.description}</p>
                  <p className="text-xs text-ink-4 mt-0.5">Filter: {activeTab.filterApplied}</p>
                </div>
                <RiskBadge level={activeTab.overallRiskLevel} />
              </div>
              )}

              {activeFactorKey !== 'R1_MMCT' && activeTab.notes?.length > 0 && (
                <div className="mb-4 text-sm text-ink-3 bg-panel-2 rounded p-3">
                  {activeTab.notes.map((n: string, i: number) => (
                    <p key={i}>{n}</p>
                  ))}
                </div>
              )}

              {activeFactorKey === 'R1_MMCT' && teamId ? (
                <R1Distribution teamId={teamId} mmctRegulator={result?.regulatorMmctLabel} />
              ) : activeFactorKey === 'M6_MARKETING' ? (
                <M6Matrix rows={activeTab.m6Matrix ?? []} year={(data as any).validation?.calendarYear} />
              ) : activeTab.mmctBlocks?.length ? (
                <MmctBlocksTable
                  blocks={activeTab.mmctBlocks}
                  collateral={activeFactorKey === 'R3_COLLATERAL'}
                  regulatorCode={result?.regulatorCode}
                />
              ) : activeFactorKey === 'R4_REVERSE' ? (
                <R4Matrix rows={activeTab.r4Matrix ?? []} />
              ) : activeFactorKey === 'R9_AA_REMA' ? (
                <R9Matrix blocks={activeTab.r9Matrix ?? []} />
              ) : activeFactorKey === 'R12_BRANCH' ? (
                // R12 cells use a simplified MMCT shape FldMatrix can't render — use the
                // dedicated r12Matrix. When it's absent (no branches, or the cached analysis
                // predates the branch import), fall back to the import/re-run empty state.
                activeTab.r12Matrix && activeTab.r12Matrix.length > 0 ? (
                  <R12Matrix rows={activeTab.r12Matrix} />
                ) : (
                  <R12EmptyState onOpenBranches={() => setBranchModalOpen(true)} teamId={teamId} />
                )
              ) : (
                activeTab.cells.length > 0 && (
                  <FldMatrix
                    cells={activeTab.cells}
                    totalCells={activeTab.totalCells}
                    isMeanComparison={activeFactorConfig?.isMean}
                    meanDecimals={activeFactorConfig?.meanDecimals}
                    meanUnit={activeFactorConfig?.meanUnit}
                    meanCurrency={activeFactorConfig?.meanCurrency}
                    onCellClick={(cell) => setDrillDown({ riskFactor: activeFactorKey, cell, lienVariant: activeTab.lienVariant, openEndVariant: activeTab.openEndVariant, channelVariant: activeTab.channelVariant })}
                  />
                )
              )}
            </div>
          ) : (
            <div className="bg-panel rounded-lg border p-12 text-center">
              <p className="text-ink-3">No data available for this risk factor.</p>
            </div>
          )}
        </div>
      )}

      {/* Drill-Down Modal */}
      {drillDown && id && (
        <FldDrillDownModal
          analysisId={id}
          riskFactor={drillDown.riskFactor}
          lienVariant={drillDown.lienVariant}
          openEndVariant={drillDown.openEndVariant}
          channelVariant={drillDown.channelVariant}
          cell={drillDown.cell}
          onClose={() => setDrillDown(null)}
        />
      )}

      {/* Branch Import Modal */}
      <BranchImportModal
        isOpen={branchModalOpen}
        onClose={() => setBranchModalOpen(false)}
      />

      {/* Partner-branded client report (firm members only) */}
      {firms.length > 0 && teamId && id && (
        <PartnerReportModal
          isOpen={clientReportOpen}
          onClose={() => setClientReportOpen(false)}
          teamId={teamId}
          analysisId={id}
          firms={firms}
        />
      )}
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function R12EmptyState({ onOpenBranches, teamId }: { onOpenBranches: () => void; teamId: string | undefined }) {
  // Probe whether branches actually exist for this team. If they do, the FLD analysis
  // was just run before the import — the cached resultJson is empty, user needs to re-run.
  const { data } = useQuery({
    queryKey: ['branches', teamId],
    queryFn: async () => {
      const res = await fairLendingApi.getBranches(teamId!);
      return res.data;
    },
    enabled: !!teamId,
  });

  const branchCount = data?.branches?.length ?? 0;

  if (branchCount > 0) {
    return (
      <div className="text-center py-8">
        <BuildingOffice2Icon className="mx-auto h-10 w-10 text-amber-500" />
        <p className="mt-3 text-sm font-medium text-ink">{branchCount} branch{branchCount === 1 ? '' : 'es'} imported, but R12 wasn't included in the last FLD analysis.</p>
        <p className="mt-1 text-xs text-ink-3">Re-run the Fair Lending analysis to populate the R12 Branch Distribution.</p>
        <div className="mt-3 flex items-center justify-center gap-2">
          <button
            onClick={onOpenBranches}
            className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium border border-rule text-ink-2 hover:bg-panel-2"
          >
            <BuildingOffice2Icon className="h-4 w-4" />
            Manage Branches
          </button>
          <Link
            to="/fair-lending"
            className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium bg-teal-600 text-white hover:bg-teal-700"
          >
            Run New FLD Analysis →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="text-center py-8">
      <BuildingOffice2Icon className="mx-auto h-10 w-10 text-ink-4" />
      <p className="mt-3 text-sm text-ink-3">Import branch locations to enable R12 analysis.</p>
      <button
        onClick={onOpenBranches}
        className="mt-3 inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium bg-teal-600 text-white hover:bg-teal-700"
      >
        <BuildingOffice2Icon className="h-4 w-4" />
        Import Branches
      </button>
    </div>
  );
}

function StatCard({ label, value, highlight }: { label: string; value: number; highlight?: boolean }) {
  return (
    <div className={clsx('rounded-lg border p-4', highlight ? 'bg-red-50 border-red-200' : 'bg-panel')}>
      <p className="text-xs text-ink-3 uppercase tracking-wider">{label}</p>
      <p className={clsx('text-2xl font-bold mt-1', highlight ? 'text-red-700' : 'text-ink')}>
        {value.toLocaleString()}
      </p>
    </div>
  );
}
